import React, { useState } from 'react';
import { Card } from '../ui';
import { UI_CONSTANTS } from '../../constants/uiConstants';
import { combineClasses } from '../../utils/formatters';

const ServiceCategoryCard = ({ category, onClick, enhanced = false, className = '' }) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (onClick) onClick(category);
  };

  if (enhanced) {
    return (
      <div 
        className={combineClasses(
          "relative group cursor-pointer",
          "transform transition-all duration-300 ease-out",
          "hover:scale-105 hover:-translate-y-2",
          className
        )}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Background Card with Gradient */}
        <div className={combineClasses(
          "relative overflow-hidden rounded-2xl",
          "bg-gradient-to-br from-white to-gray-50",
          "border border-gray-100 hover:border-orange-200",
          "shadow-sm hover:shadow-xl",
          "transition-all duration-300 ease-out",
          "h-full min-h-[180px] sm:min-h-[200px]"
        )}>
          {/* Gradient Overlay */}
          <div className={combineClasses(
            `absolute inset-0 bg-gradient-to-br ${category.color}`,
            "opacity-0 group-hover:opacity-10",
            "transition-opacity duration-300"
          )} />
          
          {/* Content */}
          <div className="relative p-6 h-full flex flex-col justify-center items-center text-center">
            {/* Icon Container */}
            <div className={combineClasses(
              "relative mb-4",
              "transform transition-transform duration-300",
              "group-hover:scale-110"
            )}>
              {/* Icon Background */}
              <div className={combineClasses(
                `w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br ${category.color}`,
                "rounded-2xl shadow-lg",
                "flex items-center justify-center text-3xl sm:text-4xl",
                "transform transition-all duration-300",
                "group-hover:shadow-xl group-hover:rotate-3"
              )}>
                {category.icon}
              </div>
              
              {/* Floating particles effect */}
              <div className={combineClasses(
                "absolute -top-1 -right-1 w-3 h-3",
                "bg-orange-400 rounded-full",
                "transform transition-all duration-500",
                isHovered ? "scale-100 opacity-100" : "scale-0 opacity-0"
              )} />
              <div className={combineClasses(
                "absolute -bottom-1 -left-1 w-2 h-2",
                "bg-yellow-400 rounded-full",
                "transform transition-all duration-700",
                isHovered ? "scale-100 opacity-100" : "scale-0 opacity-0"
              )} />
            </div>

            {/* Title */}
            <h3 className={combineClasses(
              "font-bold text-gray-800 mb-2",
              "text-sm sm:text-base lg:text-lg",
              "leading-tight",
              "group-hover:text-orange-700",
              "transition-colors duration-300"
            )}>
              {category.title}
            </h3>

            {/* Description */}
            <p className={combineClasses(
              "text-xs sm:text-sm text-gray-500 mb-3",
              "line-clamp-2",
              "group-hover:text-gray-600",
              "transition-colors duration-300"
            )}>
              {category.description}
            </p>

            {/* Service Count */}
            <div className={combineClasses(
              "flex items-center text-xs text-orange-600",
              "bg-orange-50 px-3 py-1 rounded-full",
              "border border-orange-100",
              "transition-all duration-300",
              "group-hover:bg-orange-100 group-hover:border-orange-200"
            )}>
              <span className="font-medium">
                {category.serviceCount ?? category.services?.length ?? 0} services
              </span>
            </div>

            {/* Hover Arrow */}
            <div className={combineClasses(
              "absolute bottom-4 right-4",
              "w-6 h-6 rounded-full bg-orange-500 text-white",
              "flex items-center justify-center",
              "transform transition-all duration-300",
              isHovered ? "scale-100 opacity-100 translate-x-0" : "scale-0 opacity-0 translate-x-2"
            )}>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </div>

          {/* Ripple Effect */}
          <div className={combineClasses(
            "absolute inset-0 rounded-2xl",
            "bg-gradient-to-r from-orange-400 to-amber-400",
            "opacity-0 group-active:opacity-20",
            "transition-opacity duration-150"
          )} />
        </div>

        {/* Bottom Shadow */}
        <div className={combineClasses(
          "absolute inset-x-2 bottom-0 h-4",
          "bg-gradient-to-r from-transparent via-gray-400 to-transparent",
          "blur-sm opacity-0 group-hover:opacity-30",
          "transition-opacity duration-300",
          "-z-10"
        )} />
      </div>
    );
  }

  // Original design for backward compatibility
  return (
    <Card 
      className={combineClasses(
        "hover:shadow-lg cursor-pointer group h-full flex flex-col",
        UI_CONSTANTS.TRANSITIONS.DEFAULT,
        className
      )}
      onClick={handleClick}
    >
      <Card.Content className={combineClasses(
        UI_CONSTANTS.SPACING.MD,
        "text-center flex-1 flex flex-col justify-center"
      )}>
        <div 
          className={combineClasses(
            `w-16 h-16 bg-gradient-to-br ${category.color}`,
            UI_CONSTANTS.RADIUS.LG,
            "mx-auto mb-4 flex items-center justify-center text-2xl",
            "group-hover:scale-110",
            UI_CONSTANTS.TRANSITIONS.DEFAULT
          )}
        >
          {category.icon}
        </div>
        <h3 className={combineClasses(
          "font-semibold text-gray-800 leading-tight",
          UI_CONSTANTS.TYPOGRAPHY.CAPTION
        )}>
          {category.title}
        </h3>
      </Card.Content>
    </Card>
  );
};

export default ServiceCategoryCard;
